import store from '../../store';
import PlayerTrackerTask from '../../requests/tasks/trackers/PlayerTrackerTask';
import RequestManager from '../../requests/RequestManager';
import Utils from '../../utils/Utils';
import { AAF_CONTENT_IS_AUDIO } from '../../AAFPlayer/constants';
import stopTrackingObserver from '../../AAFPlayer/helpers/StopTrackingObserver';
import AAF from '../../AAF/AAF';
import { LINEAL_CHANNELS_TTL } from '../../AAF/Resilence/TTLConstants';

const DEFAULT_TICK_INTERVAL = 60;
const TTLItem = LINEAL_CHANNELS_TTL;

class PlayerTracker {

  static trackParams = {
    isLive: false,
    deviceAttachId: null,
    offerId: null,
    purchaseId: null,
  };

  static policies = {
    tickInterval: DEFAULT_TICK_INTERVAL,
  };
  
  static urls = {
    completion: null,
    credits: null,
    dubsubchange: null,
    episodechange: null,
    error: null,
    interval: null,
    pause: null,
    resume: null,
    qualitychange: null,
    seek: null,
    stop: null,
    tick: null,
    timecode: null,
    view: null,
  };

  static currentStreamContentType = null;

  constructor() {
    this.tickIntervalId = null;
    this.lastEvent=null;
    this.tick = this.tick.bind(this);
  }

  getTrackerName() {
    return 'PlayerTracker';
  }

  setup(pgm = {}, extraParams = {}, streamContentType = null) {
    const isFallback = pgm && pgm.outputParams && pgm.outputParams.isFallback
    try {
      const { tracking } = pgm.response||pgm;
      if(!tracking){
        let newTracking = pgm.data.tracking.urls
        PlayerTracker.urls = newTracking;
      } else{
        PlayerTracker.urls = tracking.urls;
      }
      
      PlayerTracker.policies.tickInterval = tracking.policies.tick_interval;
      PlayerTracker.trackParams =  this.getTrackParams(extraParams, isFallback);
      this.lastEvent=null;
    } catch(err) {
      console.warn("PlayerTracker could not get tracking urls or tracking policies", err);
      PlayerTracker.policies.tickInterval = DEFAULT_TICK_INTERVAL;
    }

            // Reset stream anterior
    if (PlayerTracker.currentStreamContentType !== null) {
      this.setCurrentStreamContentType(null);
    }
  
    // Update stream content type
    if (streamContentType !== null) {
      this.setCurrentStreamContentType(streamContentType);
    }
  }

  static isAudio() {
    if (PlayerTracker.currentStreamContentType === null) {
        return false;
    }

    let isAudioStream = false;
    switch (PlayerTracker.currentStreamContentType) {
        case AAF_CONTENT_IS_AUDIO:
            isAudioStream = true;
            break;
        default:
            break;
    }

    return isAudioStream;
  }

  setCurrentStreamContentType(streamContentType) {
    PlayerTracker.currentStreamContentType = streamContentType;
  }


  setupExtraParams( extraParams = {}, isFallback) {
    PlayerTracker.trackParams =  this.getTrackParams(extraParams, isFallback);
  }

  getTrackParams(extraParams, isFallback) {
    try {
      const { isLive = false, groupId, deviceAttachId } = extraParams;
      if (isLive) {
        if(!isFallback) {
          AAF.TTLUtil.verifyTTLItem(TTLItem, () => { AAF.TTLUtil.setLinealChannels() }, 'PlayerTracker[getTrackParams]');
        }
        const state = store.getState();
        const linealChannels = state.linealChannels;
        const currentChannel = linealChannels.find(ch => ch.id == groupId);
        return {
          isLive,
          deviceAttachId,
          offerId: currentChannel.offerid,
          purchaseId: currentChannel.purchaseid,
        };
      } else {
        return { isLive }
      }
    } catch(e) {
      console.warn("playertracker error getting track params", e);
    }
  }

  startTicking() {
    console.log("playerTracker startTicking");
    if (this.tickIntervalId) {
      console.log("playerTracker clear this.tickInterval");
      clearInterval(this.tickIntervalId);
    }

    if (PlayerTracker.isAudio()) return;

    const interval = PlayerTracker.policies.tickInterval * 1000;

    this.tickIntervalId = setInterval(this.tick, interval);
  }

  stopTicking() {
    if (PlayerTracker.isAudio()) return;

    console.log("playerTracker stopTicking");
    clearInterval(this.tickIntervalId);
  }

  tick(time) {
    if(isNaN(time) || time<=0) {
      return;
    }
    
    if (PlayerTracker.isAudio()) return;

    console.log("playertracker sending tick as param ", time);
    this.track('tick', time);
  }

  playing(time=0) {
    console.log('######## playertracker playing', PlayerTracker.trackParams);
    this.checkLastEvent('view') && this.track('view',time);
  }

  pause(time = 0) {
    console.log('######## playertracker pause');
    this.track('pause', time);
  }

  resume(time = 0) {
    console.log('######## playertracker resume');
    this.track('resume', time);
  }

  //urrentTime, isLive, urlStop
  stop(time = 0, isLive = false, urlStop = null) {
    console.log('######## playertracker stop');
    console.log('[TSTOP] ######## playertracker stop request fired');
    this.track('stop', time, isLive,  urlStop);
  }

  bitRateChange(bitrate, bandwidth, time = 0) {
    console.log('######## playertracker qualitychange', bitrate, bandwidth, time);
    this.track('qualitychange', time);
  }

  error(e, time = 0) {
    if (PlayerTracker.isAudio()) return;
    console.log('######## playertracker error');
    this.track('error', time);
  }

  end(time = 0) {
    if (PlayerTracker.isAudio()) return;
    console.log('######## playertracker end');
    this.track('completion', time);
  }

  credits(time = 0) {
    console.log('######## playertracker credits');
    this.track('credits', time);
  }

  seek(time = 0) {
    console.log('######## playertracker seek');
    this.track('seek', time);
  }

  languageChange(time = 0) {
    console.log('######## playertracker languageChange');

    this.track('dubsubchange', time);
  }

  audioChange(time = 0) {
    console.log('######## playertracker audioChange');
    this.track('dubsubchange', time);
  }

  episodeChange(time = 0) {
    this.track('episodechange', time);
  }

  checkLastEvent(event){
    if(this.lastEvent === event){
      return false;
    }

    this.lastEvent=event;
    return true;
  }

  async track(method = '', time = 0, isLive = false, trackUrl = null) {
    // Por el momento la url adicional se usa para el trackStop
    let url = trackUrl ? trackUrl : PlayerTracker.urls[method];
    console.warn(`Playertracker request url: ${url}`);
    let params = {};
    params.timecode = (time && time.toFixed(0)) || 0;
    if (url) {
      if (PlayerTracker.trackParams && PlayerTracker.trackParams.isLive) {
        // Cuando se manda el stop, Playertracker pierde los trackParams aunque es un static attr
        // De cualquier forma en el stop no se usa alguno de los tres de abajo
        let deviceAttachId = (PlayerTracker && PlayerTracker.trackParams && PlayerTracker.trackParams.deviceAttachId);
        let offerId = (PlayerTracker && PlayerTracker.trackParams && PlayerTracker.trackParams.offerId);
        let purchaseId = (PlayerTracker && PlayerTracker.trackParams && PlayerTracker.trackParams.purchaseId);
        //const { deviceAttachId, offerId, purchaseId } = PlayerTracker.trackParams;
        if(Utils.isNotNullOrNotUndefined(deviceAttachId)) {
          params = { ...params, device_id: deviceAttachId };
        }
        console.warn(`Playertracker request trackParams:`, params);
        if (offerId && purchaseId) {
          params = { ...params, offer_id: offerId, purchase_id: purchaseId };
        }
      }
      try {
        const simpleGetRequestTask = new PlayerTrackerTask(url, params, false);
        await RequestManager.addRequest(simpleGetRequestTask);

        if(method === 'stop') {
          console.log('[TSTOP] PlayerTracker regresa de la llamada de stop');
          console.log(`[TSTOP] PlayerTracker lanza evento-observer de que termino la llamada`);
          stopTrackingObserver.fire();
        }
      } catch (err) {
        console.warn(`Playertracker request failed, url: ${url}`, err);
      }
    } else {
      console.warn(`No playertracker url found for method: ${method}`);
    }
  }

  
}

export default PlayerTracker;



// WEBPACK FOOTER //
// ./src/utils/trackers/PlayerTracker.js